import { useCallback, useEffect, useState } from 'react';
import { isNavItemActive } from './isActiveRoute';

/**
 * Collapsible sidebar sections: open/closed per section id.
 * Honors {@link section.initiallyExpanded} when a section id is first seen.
 * Expands any collapsible section that contains the active route (pathname).
 *
 * @param {Array<{ id: string, initiallyExpanded?: boolean, collapsible?: boolean, items?: Array<{ path?: string, matchPath?: string, action?: string }> }>} sections
 * @param {string} pathname - `location.pathname`
 */
export function useSectionExpandedState(sections, pathname = '') {
  const [expandedById, setExpandedById] = useState({});

  useEffect(() => {
    setExpandedById((prev) => {
      const next = { ...prev };
      let changed = false;
      sections.forEach((s) => {
        if (!Object.prototype.hasOwnProperty.call(next, s.id)) {
          next[s.id] = s.initiallyExpanded !== false;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [sections]);

  useEffect(() => {
    if (!pathname) return;
    setExpandedById((prev) => {
      const next = { ...prev };
      let changed = false;
      sections.forEach((s) => {
        if (s.collapsible === false) return;
        const items = s.items || [];
        const hasActive = items.some(
          (item) => !item.action && (item.path || item.matchPath) && isNavItemActive(item, pathname),
        );
        if (hasActive && next[s.id] === false) {
          next[s.id] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [pathname, sections]);

  const isExpanded = useCallback(
    (sectionId) =>
      Object.prototype.hasOwnProperty.call(expandedById, sectionId)
        ? expandedById[sectionId]
        : true,
    [expandedById],
  );

  const toggle = useCallback((sectionId) => {
    setExpandedById((prev) => {
      const cur = Object.prototype.hasOwnProperty.call(prev, sectionId)
        ? prev[sectionId]
        : true;
      return { ...prev, [sectionId]: !cur };
    });
  }, []);

  return { isExpanded, toggle };
}
